import type * as React from 'react';

/**
 * Optional CSS background-image controls shared by layout shells (Card, Section).
 *
 * Bare URLs are normalized to `url("...")`. Values that already look like CSS
 * images (`url(...)`, gradients, `var(...)`, etc.) or CSS keywords (`none`,
 * `inherit`, …) are left unchanged. When `backgroundImage` is set, size/repeat/
 * position default to cover / no-repeat / center unless overridden.
 */
export type BackgroundImageProps = {
  /** Image URL or CSS image value. Bare URLs are wrapped as `url("...")`. */
  backgroundImage?: string;
  backgroundSize?: 'cover' | 'contain' | 'auto' | (string & {});
  backgroundRepeat?:
    | 'no-repeat'
    | 'repeat'
    | 'repeat-x'
    | 'repeat-y'
    | 'space'
    | 'round'
    | (string & {});
  backgroundPosition?:
    | 'center'
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | (string & {});
};

const CSS_IMAGE_KEYWORD = /^(none|inherit|initial|unset|revert|revert-layer)$/i;

const CSS_IMAGE_VALUE =
  /^(url|linear-gradient|radial-gradient|conic-gradient|repeating-linear-gradient|repeating-radial-gradient|repeating-conic-gradient|image-set|-webkit-image-set|image|cross-fade|element|paint|var)\(/i;

function toCssBackgroundImage(value: string): string {
  const trimmed = value.trim();
  if (CSS_IMAGE_KEYWORD.test(trimmed) || CSS_IMAGE_VALUE.test(trimmed)) {
    return trimmed;
  }

  const escaped = trimmed.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `url("${escaped}")`;
}

/**
 * Builds a merge-friendly style object for CSS background-image props.
 * Returns `undefined` when `backgroundImage` is omitted or blank so callers
 * can skip style injection entirely.
 */
export function resolveBackgroundImageStyle(
  props: BackgroundImageProps,
): React.CSSProperties | undefined {
  const image = props.backgroundImage?.trim();
  if (!image) {
    return undefined;
  }

  return {
    backgroundImage: toCssBackgroundImage(image),
    backgroundSize: props.backgroundSize ?? 'cover',
    backgroundRepeat: props.backgroundRepeat ?? 'no-repeat',
    backgroundPosition: props.backgroundPosition ?? 'center',
  };
}
